import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Bar, Line } from 'react-chartjs-2'
import 'chart.js/auto'
import { Button, SegmentedControl, Skeleton } from '@mantine/core'
import Box from '@/components/ui/Box'
import { dashboardLocationColor, dashboardSalesColor } from '@/lib/dashboardColors'
import DashboardAssemblyActivity from './DashboardAssemblyActivity'

const seriesColor = (series) => series.location_id == null ? dashboardSalesColor : dashboardLocationColor(series.location_id)
const number = new Intl.NumberFormat('fr-FR')
const formatDate = (value) => new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric', month: 'short',
}).format(new Date(`${value}T12:00:00`))

const barValues = {
    id: 'dashboardBarValues',
    afterDatasetsDraw(chart) {
        const { ctx } = chart
        ctx.save()
        ctx.font = `500 13px ${chart.options.font.family}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'bottom'
        ctx.fillStyle = '#4b5563'

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (!chart.isDatasetVisible(datasetIndex)) return
            chart.getDatasetMeta(datasetIndex).data.forEach((bar, index) => {
                const value = dataset.data[index]
                if (!value) return
                const label = number.format(value)
                const slotWidth = chart.chartArea.width / chart.data.labels.length / chart.getVisibleDatasetCount()
                if (ctx.measureText(label).width + 6 > slotWidth) return
                ctx.fillText(label, bar.x, bar.y - 6)
            })
        })
        ctx.restore()
    },
}

function weekRanges(days) {
    const weeks = []
    days.forEach((day, index) => {
        const date = new Date(`${day}T12:00:00`)
        date.setDate(date.getDate() - (date.getDay() + 6) % 7)
        const monday = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        if (weeks.at(-1)?.monday !== monday) weeks.push({ monday, start: index, end: index })
        else weeks.at(-1).end = index
    })
    return weeks
}

const weekMarkers = {
    id: 'dashboardWeeks',
    beforeDraw(chart, args, options) {
        const { ctx, chartArea, scales: { x } } = chart
        if (!chartArea || !options.days?.length) return
        const weeks = weekRanges(options.days)
        const boundary = (index) => index <= 0 ? chartArea.left
            : index >= options.days.length ? chartArea.right
                : (x.getPixelForValue(index - 1) + x.getPixelForValue(index)) / 2
        ctx.save()
        weeks.forEach((week, index) => {
            const left = boundary(week.start)
            const right = boundary(week.end + 1)
            if (index % 2 === 0) {
                ctx.fillStyle = '#f8fafc'
                ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top)
            }
            if (index > 0) {
                ctx.strokeStyle = '#d1d5db'
                ctx.setLineDash([3, 4])
                ctx.beginPath()
                ctx.moveTo(left, chartArea.top)
                ctx.lineTo(left, chartArea.bottom)
                ctx.stroke()
            }
            ctx.font = `500 12px ${chart.options.font.family}`
            ctx.fillStyle = '#6b7280'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'bottom'
            const longLabel = `Semaine du ${formatDate(week.monday)}`
            const label = ctx.measureText(longLabel).width + 12 <= right - left ? longLabel : formatDate(week.monday)
            if (ctx.measureText(label).width + 8 <= right - left) {
                ctx.fillText(label, (left + right) / 2, chartArea.top - 12)
            }
        })
        ctx.restore()
    },
}

function ChartCard({ title, data, loading, error, retry, compact = false }) {
    const scrollRef = useRef(null)
    const tooltipRef = useRef(null)
    const hideTooltip = useCallback(() => {
        if (tooltipRef.current) tooltipRef.current.style.visibility = 'hidden'
    }, [])
    const renderTooltip = useCallback(({ chart, tooltip }) => {
        const panel = tooltipRef.current
        if (!panel) return
        if (!tooltip.opacity || !tooltip.dataPoints?.length) {
            panel.style.visibility = 'hidden'
            return
        }

        const heading = document.createElement('div')
        heading.className = 'mb-2 text-xs text-gray-500'
        heading.textContent = tooltip.title?.join(' ') || ''
        const rows = tooltip.dataPoints.map((point) => {
            const row = document.createElement('div')
            row.className = 'flex items-center gap-2 text-sm'
            const dot = document.createElement('span')
            dot.className = 'size-1.5 shrink-0 rounded-full'
            dot.style.backgroundColor = point.dataset.backgroundColor
            const label = document.createElement('span')
            label.className = 'flex-1 text-gray-600'
            label.textContent = point.dataset.label
            const value = document.createElement('span')
            value.className = 'pl-4 font-medium tabular-nums text-gray-900'
            value.textContent = number.format(point.parsed.y)
            row.append(dot, label, value)
            const group = document.createElement('div')
            group.append(row)
            const details = point.dataset.breakdowns?.[point.dataIndex] || []
            if (details.length) {
                const list = document.createElement('dl')
                list.className = 'mt-2 space-y-1 border-t border-gray-100 pt-2 text-xs text-gray-500'
                details.forEach((item) => {
                    const detail = document.createElement('div')
                    detail.className = 'flex justify-between gap-6'
                    const type = document.createElement('dt')
                    type.textContent = item.name
                    const count = document.createElement('dd')
                    count.className = 'tabular-nums text-gray-700'
                    count.textContent = number.format(item.quantity)
                    detail.append(type, count)
                    list.append(detail)
                })
                group.append(list)
            }
            return group
        })
        panel.replaceChildren(heading, ...rows)
        const bounds = chart.canvas.getBoundingClientRect()
        const x = bounds.left + tooltip.caretX
        const y = bounds.top + tooltip.caretY
        const width = panel.offsetWidth
        const height = panel.offsetHeight
        const left = x + 16 + width < window.innerWidth - 12 ? x + 16 : x - width - 16
        panel.style.left = `${Math.max(12, Math.min(left, window.innerWidth - width - 12))}px`
        panel.style.top = `${Math.max(12, Math.min(y - height / 2, window.innerHeight - height - 12))}px`
        panel.style.visibility = 'visible'
    }, [])

    useEffect(() => {
        const dismissOnEscape = (event) => {
            if (event.key === 'Escape') hideTooltip()
        }
        window.addEventListener('scroll', hideTooltip, true)
        window.addEventListener('resize', hideTooltip)
        window.addEventListener('pointerdown', hideTooltip)
        window.addEventListener('keydown', dismissOnEscape)
        return () => {
            window.removeEventListener('scroll', hideTooltip, true)
            window.removeEventListener('resize', hideTooltip)
            window.removeEventListener('pointerdown', hideTooltip)
            window.removeEventListener('keydown', dismissOnEscape)
        }
    }, [hideTooltip])
    const [hiddenSeries, setHiddenSeries] = useState([])
    const hasData = data?.series?.some((series) => series.values.some((value) => value !== 0))
    const total = data?.series?.reduce((sum, series) => sum + series.values.reduce((a, b) => a + b, 0), 0)
    const Chart = compact ? Line : Bar
    const seriesCount = data?.series?.length || 1
    const chartMinWidth = compact ? 0 : Math.max(420, (data?.days?.length || 0) * seriesCount * 10 + 60)
    const subtitle = data ? `Du ${formatDate(data.start)} au ${formatDate(data.end)}` : undefined

    useLayoutEffect(() => {
        const viewport = scrollRef.current
        if (!viewport) return

        let followLatest = true
        const scrollToLatest = () => {
            if (followLatest) viewport.scrollLeft = viewport.scrollWidth - viewport.clientWidth
        }
        const trackPosition = () => {
            followLatest = viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft < 8
        }
        scrollToLatest()
        const observer = new ResizeObserver(scrollToLatest)
        observer.observe(viewport)
        observer.observe(viewport.firstElementChild)
        viewport.addEventListener('scroll', trackPosition, { passive: true })
        return () => {
            observer.disconnect()
            viewport.removeEventListener('scroll', trackPosition)
        }
    }, [hasData, loading, data?.start, data?.end])

    return (
        <Box title={title} subtitle={subtitle} rightSection={data && (
            <span className="text-sm text-gray-500 tabular-nums">{number.format(total)}</span>
        )}>
            {loading ? <Skeleton height={compact ? 180 : 'clamp(320px, 35vw, 560px)'} radius="md" /> : error && !data ? (
                <div className="h-40 flex flex-col items-center justify-center gap-3" role="alert">
                    <p className="text-sm text-gray-500">Impossible de charger les données.</p>
                    <Button variant="default" onClick={retry}>Réessayer</Button>
                </div>
            ) : !hasData ? (
                <p className="py-12 text-center text-sm text-gray-500">Aucune donnée sur cette période.</p>
            ) : (
                <div className="@container min-w-0 max-w-full overflow-hidden">
                    {data.series.length > 1 && (
                        <div className="flex flex-wrap justify-start gap-x-3 gap-y-1">
                            {data.series.map((series) => (
                                <button
                                    key={series.name}
                                    type="button"
                                    aria-pressed={!hiddenSeries.includes(series.name)}
                                    onClick={() => setHiddenSeries((current) => current.includes(series.name)
                                        ? current.filter((name) => name !== series.name)
                                        : [...current, series.name])}
                                    className="inline-flex min-h-11 items-center gap-2 rounded px-2 text-sm text-gray-500 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-blue-500"
                                >
                                    <span className="size-2 rounded-full" style={{ backgroundColor: seriesColor(series), opacity: hiddenSeries.includes(series.name) ? 0.3 : 1 }} aria-hidden="true" />
                                    <span className={hiddenSeries.includes(series.name) ? 'line-through' : undefined}>{series.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div ref={scrollRef} className="overflow-x-auto" tabIndex={0} role="region" aria-label={`${title} : défilement par date`}>
                    <div style={{ minWidth: chartMinWidth, height: compact ? 180 : 'clamp(320px, 35cqw, 560px)' }}>
                    <Chart
                        onMouseLeave={hideTooltip}
                        plugins={compact ? [weekMarkers] : [weekMarkers, barValues]}
                        aria-label={title}
                        role="img"
                        data={{
                            labels: data.days.map(formatDate),
                            datasets: data.series.map((series) => ({
                                label: series.name, data: series.values,
                                breakdowns: series.breakdowns,
                                hidden: hiddenSeries.includes(series.name),
                                backgroundColor: seriesColor(series),
                                borderColor: seriesColor(series),
                                borderWidth: compact ? 2 : 0,
                                pointRadius: compact ? 2 : 0,
                                pointHitRadius: 12,
                                tension: 0.2,
                                borderRadius: 1,
                                categoryPercentage: 0.85,
                                barPercentage: 0.9,
                            })),
                        }}
                        options={{
                            responsive: true, maintainAspectRatio: false, locale: 'fr-FR',
                            layout: { padding: { top: 44, right: 12, bottom: 8, left: 8 } },
                            plugins: {
                                dashboardWeeks: { days: data.days },
                                legend: { display: false },
                                tooltip: {
                                    enabled: false,
                                    external: renderTooltip,
                                    animation: false,
                                    animations: false,
                                    mode: 'nearest',
                                    intersect: !compact,
                                },
                            },
                            scales: {
                                x: { grid: { display: false }, border: { display: false }, ticks: { maxRotation: 0, maxTicksLimit: data.days.length > 14 ? 5 : 7, color: '#6b7280' } },
                                y: { beginAtZero: true, grace: '10%', border: { display: false }, grid: { color: '#f3f4f6' }, ticks: { precision: 0, maxTicksLimit: 8, padding: 10, color: '#6b7280', callback: (value) => number.format(value) } },
                            },
                        }}
                    />
                    </div>
                    </div>
                    <table className="sr-only">
                        <caption>{title}, {subtitle}</caption>
                        <thead><tr><th>Date</th>{data.series.map((series) => <th key={series.name}>{series.name}</th>)}</tr></thead>
                        <tbody>{data.days.map((day, index) => <tr key={day}><th>{formatDate(day)}</th>{data.series.map((series) => <td key={series.name}>{series.values[index]}</td>)}</tr>)}</tbody>
                    </table>
                </div>
            )}
            {createPortal(
                <div
                    ref={tooltipRef}
                    role="tooltip"
                    className="pointer-events-none fixed z-[400] max-w-[calc(100vw-24px)] space-y-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
                    style={{ visibility: 'hidden' }}
                />,
                document.body
            )}
            {error && data && <p className="mt-3 text-sm text-gray-500" role="status">Actualisation impossible. Les dernières données restent affichées.</p>}
        </Box>
    )
}

export default function DashboardCharts({ data, loading, error, retry }) {
    const [period, setPeriod] = useState('7')
    const props = { loading, error, retry }
    const channels = [
        { id: 'website', title: 'Site internet' },
        { id: 'pos', title: 'Boutique Paris' },
    ]
    return (
        <section className="space-y-5" aria-label="Activité">
            <ChartCard title="Préparations faites" data={data?.preparations} {...props} />
            <ChartCard title="Montres en écrin préparées" data={data?.watch_quantities} {...props} />
            <ChartCard title="Bracelets et autres articles préparés" data={data?.other_quantities} {...props} />
            <DashboardAssemblyActivity production_orders={data?.assembly_activity} error={error} retry={retry} />
            <section className="space-y-3" aria-label="Ventes Shopify">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-medium">Ventes Shopify</h2>
                    <SegmentedControl size="xs" value={period} onChange={setPeriod} data={[
                        { value: '7', label: '7 jours' },
                        { value: '30', label: '30 jours' },
                    ]} aria-label="Période des ventes" />
                </div>
                <div className="grid grid-cols-1 @5xl:grid-cols-2 gap-5 [&>*]:min-w-0">
                    {channels.map((channel) => {
                        const days = data?.sales?.channels?.find((item) => item.id === channel.id)?.days.slice(-Number(period))
                        const chart = days?.length ? {
                            start: days[0].date,
                            end: days[days.length - 1].date,
                            days: days.map((day) => day.date),
                            series: [{ name: 'Commandes', values: days.map((day) => day.orders) }],
                        } : undefined
                        return <ChartCard key={channel.id} title={channel.title} data={chart} compact {...props} />
                    })}
                </div>
                <p className="text-xs text-gray-500">Commandes payées, remboursements inclus. Hors commandes test et annulées. Dates en heure de Paris.</p>
            </section>
        </section>
    )
}
